import { useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'

const InstallApp = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [status, setStatus] = useState('Checking install availability')
  const [isInstalled, setIsInstalled] = useState(false)

  const isIos = useMemo(() => /iphone|ipad|ipod/i.test(window.navigator.userAgent), [])

  useEffect(() => {
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true

    if (standalone) {
      setIsInstalled(true)
      setStatus('adnexio HR is already installed')
      return undefined
    }

    if (isIos) {
      setStatus('On iPhone or iPad, tap Share, then Add to Home Screen')
    } else {
      setStatus('Open this page in Chrome or Edge if install is not available yet')
    }

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault()
      setDeferredPrompt(event)
      setStatus('Ready to install')
    }

    const handleAppInstalled = () => {
      setDeferredPrompt(null)
      setIsInstalled(true)
      setStatus('Installation completed')
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [isIos])

  const handleInstall = async () => {
    if (!deferredPrompt) {
      setStatus('Install is not available yet. Try again after the page finishes loading')
      return
    }

    deferredPrompt.prompt()
    const choice = await deferredPrompt.userChoice
    setDeferredPrompt(null)

    if (choice.outcome === 'accepted') {
      setStatus('Confirming installation')
      return
    }

    setStatus('Installation was dismissed. You can try again from this page')
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#F7F9FA',
        display: 'grid',
        placeItems: 'center',
        px: 2,
      }}
    >
      <Dialog
        open
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 18px 42px rgba(24, 36, 56, 0.14)',
          },
        }}
      >
        <DialogContent sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
          <Box
            component="img"
            src="/adnexio-logo-text.svg"
            alt="adnexio HR"
            sx={{
              display: 'block',
              height: 34,
              width: 'auto',
              mx: 'auto',
              mb: 4,
            }}
          />

          <Typography
            component="h1"
            sx={{
              color: '#262626',
              fontFamily: 'Poppins',
              fontSize: { xs: 24, sm: 28 },
              fontWeight: 700,
              lineHeight: 1.25,
              letterSpacing: 'initial',
              mb: 1.5,
            }}
          >
            Install adnexio HR
          </Typography>

          <Typography
            sx={{
              color: '#636363',
              fontFamily: 'Poppins',
              fontSize: 14,
              lineHeight: '22px',
              letterSpacing: 'initial',
              mb: 3,
            }}
          >
            Open adnexio HR from your device home screen with a faster app-like experience.
          </Typography>

          {!isIos && (
            <Button
              fullWidth
              variant="contained"
              disabled={isInstalled || !deferredPrompt}
              onClick={handleInstall}
              sx={{
                minHeight: 48,
                borderRadius: '28px',
                bgcolor: '#F3BF3A',
                color: '#262626',
                fontFamily: 'Poppins',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#D09A01',
                  boxShadow: 'none',
                },
                '&:disabled': {
                  bgcolor: '#E5E5E5',
                  color: '#8A8A8A',
                },
              }}
            >
              {isInstalled ? 'adnexio HR installed' : 'Install adnexio HR'}
            </Button>
          )}

          <Typography
            role="status"
            aria-live="polite"
            sx={{
              minHeight: 20,
              color: '#8A8A8A',
              fontFamily: 'Poppins',
              fontSize: 12,
              lineHeight: '18px',
              letterSpacing: 'initial',
              mt: 2,
              mb: 0,
            }}
          >
            {status}
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default InstallApp
